import axios from 'axios'
import { API_URL, MARKET_ROYALTY_ADDRESS } from '@/utils/constants'
import Wallet, { ensureEccLib } from '@/utils/wallet'
import {
	assertSecureWalletSupported,
	compressedPublicKey,
	PASSTHROUGH_ESCROW_POLICY,
	requireBuild3,
	SecurePurchaseUnavailableError,
	type SecureAssetProtocol
} from './secure-purchase'
import { buyerScriptsForAddresses, unsignedTxHexFromPsbt, type SecureInvoiceBreakdown, type SecurePurchaseItemBreakdown } from './secure-psbt'
import { purchaseSignPlans, signOwnedPsbts } from './secure-sign'
import { readSecureTicket, storeSecureTicket } from './secure-ticket-store'
import { requirePinnedCosigner } from './secure-escrow'
import { verifySaleChain, verifySetup, type SaleParent } from './secure-passthrough'

/**
 * Passthrough v4 purchase. One sale transaction spends every chosen
 * passthrough (already seller- and Ordinals-Wallet-signed) plus two of the
 * buyer's UTXOs. The buyer verifies the whole sale locally, then signs only
 * their two inputs. Up to 20 items per sale (the network caps a transaction at
 * 25 inputs and two are the buyer's).
 */
export const MAX_PROTECTED_ITEMS_PER_PURCHASE = 12

type SecurePsbtRole = {
	role: string
	index: number
	psbt: string
}

/** What the buyer was shown for one listing, and what the sale must pay. */
type SecurePurchaseListing = {
	sellerAddress: string
	creatorAddress?: string | null
	/** Buyer-facing price: seller payout + marketplace fee must not exceed it. */
	satoshiPrice?: number
}

export type SecurePurchaseQuote = {
	/** The sale txid, fixed at build (segwit txids ignore witnesses). Doubles as the purchase handle. */
	saleTxid: string
	protocol: SecureAssetProtocol
	feeRateSatVb: number
	/** The co-signed sale PSBT; only the buyer's two inputs are open. */
	psbt: string
	/** One entry, the sale, for callers that display "the purchase PSBT". */
	purchases: SecurePsbtRole[]
	/** Unsigned sale hex for display / the local store. */
	ticketHex: string
	/** One single-item sale per listing, in order; each after the first spends the previous one's change. */
	sales: Array<{ saleTxid: string; psbt: string; parent: SaleParent }>
	/** Buyer setup transaction when the wallet's UTXOs had to be split first; signed in the same prompt. */
	setup?: { txid: string; psbt: string }
	outpoints: string[]
	listings: SecurePurchaseListing[]
	totals: {
		seller_sats: number
		ow_fee_sats: number
		creator_royalty_sats: number
		miner_fee_sats: number
		total_sats: number
	}
}

function buyerAddresses(): string[] {
	const taproot = Wallet.getTaprootAddress()
	const balance = Wallet.balanceAddress()
	return Array.from(new Set([taproot, balance].filter(Boolean)))
}

function buyerDestination(): string {
	return Wallet.getTaprootAddress() || Wallet.balanceAddress()
}

function verifyQuote(quote: {
	sales: Array<{ saleTxid: string; psbt: string; parent: SaleParent }>
	setup?: { txid: string; psbt: string }
	feeRateSatVb: number
	outpoints: string[]
	listings: SecurePurchaseListing[]
}) {
	let setupFeeSat = 0
	if (quote.setup) {
		const setup = verifySetup({ setupPsbtHex: quote.setup.psbt, buyerAddresses: buyerAddresses(), feeRateSatVb: quote.feeRateSatVb })
		if (setup.txid !== quote.setup.txid) {
			throw new SecurePurchaseUnavailableError('invalid_sale', 'Setup transaction does not hash to its declared txid')
		}
		setupFeeSat = setup.feeSat
	}
	const verified = verifySaleChain({
		setupTxid: quote.setup?.txid,
		links: quote.sales.map((sale, index) => ({
			salePsbtHex: sale.psbt,
			parent: sale.parent,
			listing: {
				outpoint: quote.outpoints[index],
				sellerAddress: quote.listings[index].sellerAddress,
				creatorAddress: quote.listings[index].creatorAddress,
				satoshiPrice: quote.listings[index].satoshiPrice
			}
		})),
		buyerAddresses: buyerAddresses(),
		marketFeeAddress: MARKET_ROYALTY_ADDRESS
	})
	verified.saleTxids.forEach((txid, index) => {
		if (txid !== quote.sales[index].saleTxid) {
			throw new SecurePurchaseUnavailableError('invalid_sale', `Sale ${index + 1} does not hash to its declared txid`)
		}
	})
	if (setupFeeSat > 0) {
		verified.networkFeeSat += setupFeeSat
		verified.totalSat += setupFeeSat
	}
	return verified
}

/** Buyer-facing text for the codes the build endpoint returns. */
function securePurchaseFailureMessage(code: string): string {
	switch (code) {
		case 'two_funding_utxos_required':
			return 'A protected purchase needs at least two spendable UTXOs in your wallet; send yourself a small amount first, then try again.'
		case 'insufficient_funds':
			return 'Not enough spendable balance: your UTXOs must cover the item prices plus fees, with at least one UTXO on each side of the sale.'
		case 'listing_not_found':
		case 'listing_source_spent':
		case 'listing_changed':
			return 'One of the items is no longer available.'
		case 'listings_conflict':
			return 'Two of these listings cannot be bought together; buy them separately.'
		case 'cosigner_rotated':
		case 'cosigner_unavailable':
			return 'Protected purchases are temporarily unavailable.'
		case 'asset_index_stale':
		case 'asset_index_unavailable':
		case 'asset_index_incomplete':
		case 'asset_index_disagrees':
			// Our index briefly trails a new block; we refuse rather than build
			// a sale on stale ownership data. It clears on its own.
			return 'Our index is catching up with the latest block. Try again in a moment.'
		case 'too_many_pending_purchases':
			return 'This wallet has too many unfinished protected purchases; wait a few minutes and try again.'
		case 'too_many_items':
			return `Up to ${MAX_PROTECTED_ITEMS_PER_PURCHASE} protected items can be bought together.`
		default:
			return 'Protected purchase unavailable right now.'
	}
}

export async function buildSecurePurchase(input: {
	outpoints: string[]
	protocol: SecureAssetProtocol
	feeRate: number
	/** One per outpoint, same order. */
	listings: SecurePurchaseListing[]
}): Promise<SecurePurchaseQuote> {
	assertSecureWalletSupported(Wallet.type())
	await ensureEccLib()
	const caps = await requireBuild3(input.protocol)
	requirePinnedCosigner(caps)
	if (!Number.isFinite(input.feeRate) || input.feeRate <= 0) {
		throw new SecurePurchaseUnavailableError('invalid_fee_rate', 'Fee rate must be a positive sat/vB value')
	}
	if (input.outpoints.length === 0) {
		throw new SecurePurchaseUnavailableError('no_outpoints', 'Select at least one item')
	}
	if (input.outpoints.length > MAX_PROTECTED_ITEMS_PER_PURCHASE) {
		throw new SecurePurchaseUnavailableError('too_many_items', securePurchaseFailureMessage('too_many_items'))
	}
	if (input.listings.length !== input.outpoints.length) {
		throw new SecurePurchaseUnavailableError('invalid_sale', 'Every item needs its listing details')
	}
	const from = Wallet.provider.capabilities.includes('payments-wallet')
		? Wallet.balanceAddress()
		: Wallet.getTaprootAddress()
	const payload: Record<string, unknown> = {
		outpoints: input.outpoints,
		protocol: input.protocol,
		from,
		public_key: compressedPublicKey(Wallet.getPublicKey()),
		fee_rate: input.feeRate,
		wallet_type: Wallet.type() || undefined
	}
	if (Wallet.provider.capabilities.includes('payments-wallet')) {
		payload.to = Wallet.getTaprootAddress()
	}
	const { data, status } = await axios.post(`${API_URL}/wallet/secure-purchase/build`, payload, {
		validateStatus: () => true,
		headers: { 'Cache-Control': 'no-store' }
	})
	if (status >= 400) {
		const code = String(data?.code || `http_${status}`)
		throw new SecurePurchaseUnavailableError(code, data?.message || securePurchaseFailureMessage(code))
	}
	if (String(data?.policy || '') !== PASSTHROUGH_ESCROW_POLICY || !Array.isArray(data?.sales)) {
		throw new SecurePurchaseUnavailableError('invalid_sale', 'Secure purchase build did not return a passthrough sale')
	}
	const isHex64 = (v: unknown) => typeof v === 'string' && /^[0-9a-f]{64}$/i.test(v)
	const sales = (data.sales as any[]).map((row) => {
		const parent = row?.parent
		if (!isHex64(row?.sale_txid) || typeof row?.psbt !== 'string' || !isHex64(parent?.txid) || typeof parent?.raw !== 'string' || typeof parent?.source_outpoint !== 'string') {
			throw new SecurePurchaseUnavailableError('invalid_sale', 'Secure purchase build returned a malformed sale')
		}
		return {
			saleTxid: String(row.sale_txid).toLowerCase(),
			psbt: String(row.psbt),
			parent: { txid: String(parent.txid).toLowerCase(), raw: String(parent.raw), source_outpoint: String(parent.source_outpoint) } as SaleParent
		}
	})
	if (sales.length !== input.outpoints.length) {
		throw new SecurePurchaseUnavailableError('invalid_sale', 'Secure purchase build returned the wrong number of sales')
	}
	const saleTxid = sales[0].saleTxid
	const unsignedHex = unsignedTxHexFromPsbt(sales[0].psbt)
	const setup =
		data?.setup && isHex64(data.setup.txid) && typeof data.setup.psbt === 'string'
			? { txid: String(data.setup.txid).toLowerCase(), psbt: String(data.setup.psbt) }
			: undefined
	const economics = (data?.economics || {}) as Record<string, unknown>
	const quote: SecurePurchaseQuote = {
		saleTxid,
		protocol: input.protocol,
		feeRateSatVb: input.feeRate,
		psbt: sales[0].psbt,
		purchases: sales.map((s, index) => ({ role: 'sale', index, psbt: s.psbt })),
		ticketHex: unsignedHex,
		sales,
		setup,
		outpoints: input.outpoints,
		listings: input.listings,
		totals: {
			seller_sats: Number(economics.total_price_sats || 0),
			ow_fee_sats: Number(economics.ow_fee_sats || 0),
			creator_royalty_sats: Number(economics.creator_royalty_sats || 0),
			miner_fee_sats: Number(economics.miner_fee_sats || 0),
			total_sats: Number(economics.buyer_total_sats || 0)
		}
	}
	// Verify the whole sale before the quote is ever shown or signed, and
	// reconcile the API's own numbers with what the transaction actually pays.
	const verified = verifyQuote(quote)
	quote.totals = {
		seller_sats: verified.sellerProceedsSat,
		ow_fee_sats: verified.marketFeeSat,
		creator_royalty_sats: verified.creatorRoyaltySat,
		miner_fee_sats: verified.networkFeeSat,
		total_sats: verified.totalSat
	}
	storeSecureTicket({
		graphDigest: saleTxid,
		t2Hex: unsignedHex,
		buyerAddress: buyerDestination(),
		feeRateSatVb: input.feeRate,
		storedAt: Date.now()
	})
	return quote
}

export type SignedSecurePurchase = {
	quote: SecurePurchaseQuote
	/** Per link, in order; the first carries the signed setup when there is one. */
	sales: Array<{ sale_txid: string; psbt: string; setup_psbt?: string }>
}

/**
 * Sign the purchase: one wallet prompt covering the setup (if any) and the
 * buyer's funding inputs in every link, SIGHASH_ALL only. Nothing is sent.
 * The sale is re-verified right before the prompt, and the stored copy guards
 * against a quote mutated between build and sign.
 */
export async function signSecurePurchase(quote: SecurePurchaseQuote): Promise<SignedSecurePurchase> {
	assertSecureWalletSupported(Wallet.type())
	await ensureEccLib()
	const stored = readSecureTicket(quote.saleTxid)
	if (stored && stored.t2Hex !== unsignedTxHexFromPsbt(quote.psbt)) {
		throw new SecurePurchaseUnavailableError('sale_mismatch', 'Sale changed since it was built; rebuild the purchase')
	}
	verifyQuote(quote)
	const buyerScripts = buyerScriptsForAddresses(buyerAddresses())
	const toSign = [...(quote.setup ? [quote.setup.psbt] : []), ...quote.sales.map((s) => s.psbt)]
	const plans = purchaseSignPlans(toSign, buyerScripts)
	const signed = await signOwnedPsbts(plans)
	if (signed.length !== toSign.length) {
		throw new SecurePurchaseUnavailableError('sign_failed', 'Wallet did not sign the purchase')
	}
	const signedSetup = quote.setup ? signed.shift() : undefined
	return {
		quote,
		sales: quote.sales.map((s, index) => ({
			sale_txid: s.saleTxid,
			psbt: signed[index],
			...(index === 0 && signedSetup ? { setup_psbt: signedSetup } : {})
		}))
	}
}

/** Broadcast a signed purchase: the API proves every link and submits the packages in order. */
export async function broadcastSecurePurchase(signed: SignedSecurePurchase): Promise<{ txid: string }> {
	const { data, status } = await axios.post(
		`${API_URL}/market/secure-purchase/submit`,
		{ sales: signed.sales },
		{ validateStatus: () => true, headers: { 'Cache-Control': 'no-store' } }
	)
	if (status >= 400) {
		throw new SecurePurchaseUnavailableError(
			data?.code || `http_${status}`,
			data?.message || 'Secure purchase submit failed'
		)
	}
	return { txid: String(data?.txid || signed.quote.saleTxid) }
}

/** Sign and broadcast in one step, for callers without a confirmation screen in between. */
export async function submitSecurePurchase(quote: SecurePurchaseQuote): Promise<{ txid: string }> {
	return broadcastSecurePurchase(await signSecurePurchase(quote))
}

/** Invoice amounts, re-derived from the quote's own verified transaction. */
export function secureInvoice(
	quote: SecurePurchaseQuote
): SecureInvoiceBreakdown & { items: SecurePurchaseItemBreakdown[] } {
	return verifyQuote(quote)
}
